import React, { FunctionComponent } from "react";
import { ICardElementWithChildren } from "backend/model/model";
import TextElement from "reportengine/TextElement";
import Text from "reportengine/Text";
import Report from "reportengine/Report";
import Slide from "reportengine/Slide";
import { createReport, saveBlob } from "reportengine/createReport";
import reportContextToRequest from "./reportContextToRequest";
import DomElement from "reportengine/DomElement";
import ProgressModal from "design/components/progress/ProgressModal";
import Portion from "reportengine/Portion";
import useHierarchy from "backend/hooks/elements/useHierarchy";
import flattenHierarchy from "backend/flattenHierarchy";
import KPISlide from "./slides/KPISlide";
import OverallPerformanceCard from "design/components/cards/OverallPerformanceCard";
import ElementOverviewDonutCard from "design/components/cards/ElementOverviewDonutCard";
import KPIPerformanceTable from "./slides/KPIPerformanceTable";
import InitiativePerformanceTable from "./slides/InitiativePerformanceTable";
import InitiativeSlide from "./slides/InitiativeSlide";
import { useSelectedReportContext } from "state/SelectedReportContext";

const StrategyReport = (
  props: React.PropsWithChildren<{
    item: ICardElementWithChildren;
    filetype: string;
    onClose?: () => void;
  }>
) => {
  const { item, filetype } = props;
  const {
    isLoading: isChildrenLoading,
    data: children,
    isPreviousData,
    isRefetching,
  } = useHierarchy(item.id);
  const [status, setStatus] = React.useState("Generating Report");
  const [progress, setProgres] = React.useState(0);
  const [blob, setBlob] = React.useState<null | Blob>(null);
  const reportSelection = useSelectedReportContext();

  /**
   * This flag is only for testing pages faster
   */
  const disableDetailsPages = false;

  const kpis = React.useMemo(() => {
    return flattenHierarchy(children).filter((item) => item.typename === "KPI");
  }, [children]);

  const initiatives = React.useMemo(() => {
    return flattenHierarchy(children).filter(
      (item) => item.typename === "Initiative"
    );
  }, [children]);

  if (isChildrenLoading || isPreviousData || isRefetching) {
    return (
      <ProgressModal
        isOpen
        onClose={() => {
          if (props.onClose) {
            props.onClose();
          }
        }}
        isComplete={!!blob}
        cancelClickHandler={props.onClose}
        completedClickHandler={() => {
          if (blob) {
            saveBlob(`${item.name}.${filetype}`, blob);
          }
        }}
        downloadText="Download"
        progress={progress}
        statusText={"Initializing..."}
      />
    );
  }
  return (
    <div>
      <Report
        template="/templates/report_en.pptx"
        onNothingToRender={() => {
          setStatus("Nothing to render");
        }}
        onProgress={(value) => {
          setProgres(value);
          console.log("slide progress ", value);
        }}
        onReadyToRender={async (report) => {
          setProgres(95);
          const blob = await createReport(
            await reportContextToRequest(report, item.symbol, filetype)
          );
          setStatus("Report ready");
          setBlob(blob);
          setProgres(100);
        }}
      >
        <Slide index={0}>
          <TextElement slideElement="title">
            {reportSelection.report?.reportType?.name || "Strategy Report"}
          </TextElement>
          <TextElement slideElement="subtitle">{item.name}</TextElement>
          <TextElement slideElement="footer">
            {item.period?.name || ""}
          </TextElement>
        </Slide>
        <Slide index={1}>
          <TextElement slideElement="heading_1_text">
            Overall Performance Summary
          </TextElement>
          <TextElement slideElement="heading_2_text">
            KPI Performance Summary
          </TextElement>
          <TextElement slideElement="heading_3_text">
            Initiative Performance Summary
          </TextElement>
          <Text slideElement="period">
            <Portion paragraphIndex={0} portionIndex={0}>
              {item.period?.name || ""}
            </Portion>
          </Text>
          <TextElement slideElement="footer">{item.scorecardname}</TextElement>
        </Slide>
        <Slide index={2}>
          <TextElement slideElement="section_number">01</TextElement>
          <TextElement slideElement="title">
            Overall Performance Summary
          </TextElement>
          <TextElement slideElement="footer">{item.scorecardname}</TextElement>
        </Slide>
        <Slide index={3}>
          <Text slideElement="period">
            <Portion paragraphIndex={0} portionIndex={0}>
              {item.period?.name || ""}
            </Portion>
          </Text>
          <TextElement slideElement="footer">{item.scorecardname}</TextElement>
          <TextElement slideElement="overall_pefrormance">
            Overall Performance YTD
          </TextElement>
          <TextElement slideElement="kpi_performance_overview">
            KPI Performance Overview YTD
          </TextElement>
          <TextElement slideElement="corporate_overall_performance">
            Initiative Performance Overview
          </TextElement>
          <DomElement
            typename="image"
            selector=".chart"
            slideElement="overall_performance_chart"
            transform={{
              width: 70,
              height: 70,
            }}
          >
            <div style={{ width: "500px", height: "300px" }}>
              <OverallPerformanceCard
                key="PERF_W_YTD"
                titleKey="PERF_W_YTD_prettyvalue"
                element={item}
                disableAnimation
                periodName={item.period?.name || ""}
              />
            </div>
          </DomElement>
          <DomElement
            typename="image"
            selector=".chart"
            slideElement="kpi_performance_chart"
            transform={{
              x: -90 + 20,
              y: -90,
              height: 180,
              width: 180,
            }}
          >
            {" "}
            <ElementOverviewDonutCard
              title="KPI Overview"
              data={children!}
              typenames={["KPI"]}
              disableAnimation
              periodName={item.period?.name || ""}
            />{" "}
          </DomElement>
          <DomElement
            typename="image"
            selector=".chart"
            slideElement="corporate_overall_performance_chart"
            transform={{
              x: -90,
              y: -90,
              height: 180,
              width: 180,
            }}
          >
            {" "}
            <ElementOverviewDonutCard
              title="Initiative Overview"
              data={children!}
              typenames={["Initiative"]}
              disableAnimation
              periodName={item.period?.name || ""}
            />{" "}
          </DomElement>
        </Slide>

        <Slide index={4}>
          <TextElement slideElement="title">
            KPI Performance Summary
          </TextElement>
          <TextElement slideElement="footer">{item.scorecardname}</TextElement>
        </Slide>

        <KPIPerformanceTable kpis={kpis} report={item} />

        {!disableDetailsPages &&
          kpis.map((kpi) => {
            return <KPISlide item={kpi} />;
          })}

        <Slide index={7}>
          <TextElement slideElement="section_number">03</TextElement>
          <TextElement slideElement="title">
            Initiative Performance Summary
          </TextElement>
          <TextElement slideElement="footer">{item.scorecardname}</TextElement>
        </Slide>

        <InitiativePerformanceTable items={initiatives} report={item} />

        {!disableDetailsPages &&
          initiatives.map((initiative) => {
            return <InitiativeSlide item={initiative} />;
          })}

        <Slide index={10}>
          <TextElement slideElement="subtitle">{item.name}</TextElement>
          <TextElement slideElement="title">Performance Report</TextElement>
        </Slide>
      </Report>
      <ProgressModal
        isOpen
        onClose={() => {
          if (props.onClose) {
            props.onClose();
          }
        }}
        isComplete={!!blob}
        cancelClickHandler={props.onClose}
        completedClickHandler={() => {
          if (blob) {
            saveBlob(`${item.name}.${filetype}`, blob);
          }
        }}
        downloadText="Download"
        progress={progress}
        statusText={status}
      />
    </div>
  );
};

export default StrategyReport;
